import React, { useContext, useEffect, useRef } from 'react';

import makeStyles from '@material-ui/core/styles/makeStyles';

import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import TextInput from '../../../Inputs/TextInput';
import ControlledSelectInput from '../../../Controlled/ControlledSelect';
import ControlledCheckbox from '../../../Controlled/ControlledCheckbox';

import { UserContext } from '../../../../Contexts/UserContext';
import {
  SetCurrentStepIndexContext,
  StepperDataContext,
  StepperDataDispatchContext,
  CurrentStepIndexContext
} from '../../../../Contexts/StepperContext';

import { ROUTE_NAMES } from '../../../../Routes/Routes';
import {
  TERMS,
  TERMS_ROUTES
} from '../../../../Views/Information/Information/Information';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import * as yup from 'yup';
import { PrivacyPoliciesLink } from '../../../../Configs/Links';
import {
  IDENTIFICATION_OPTION_FULL_LABEL,
  IDENTIFICATION_TYPE,
  IDENTIFICATION_TYPE_LABELS
} from '../../../../Enums/users';
import Recaptcha from '../../../Captcha/TurnstileManagedCaptcha';
import { isPrintableAscii } from '../../../../Utils/Misc/String';
import { NAMES_REGEX } from '../../../../Utils/Misc/RegEx';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCaptcha } from '../../../../Hooks/useCaptcha';

const IDENTIFICATION_OPTIONS = IDENTIFICATION_TYPE_LABELS.Full;

const schema = yup.object({
  email: yup
    .string()
    .trim()
    .max(100, 'Máximo 100 carácteres')
    .email('El correo debe de ser válido')
    .test(
      'is-ascii',
      'Este campo no puede contener tildes ni caracteres especiales',
      isPrintableAscii
    )
    .lowercase()
    .required('Ingresa el correo'),
  validateEmail: yup
    .string()
    .lowercase()
    .oneOf([yup.ref('email')], 'El correo debe ser el mismo')
    .required('Ingresa el correo nuevamente'),
  fullName: yup
    .string()
    .trim()
    .matches(NAMES_REGEX, 'No se permiten números ni caracteres especiales')
    .max(150, 'Máximo 150 caracteres')
    .required('Ingresa el nombre completo'),
  identificationType: yup
    .string()
    .oneOf(IDENTIFICATION_OPTIONS.map(idType => idType.value)),
  identification: yup.mixed().when('identificationType', {
    is: IDENTIFICATION_TYPE.PP,
    then: yup
      .string()
      .trim()
      .max(15, 'Máximo 15 carácteres en el pasaporte')
      .matches(/^[0-9a-zA-Z]+$/, 'Ingresa un número de pasaporte válido')
      .required('Ingresa el número de tu pasaporte'),
    otherwise: yup
      .string()
      .trim()
      .matches(/^[0-9]+$/, 'Ingresa un número de identificación válido')
      .max(10, 'Máximo 10 dígitos')
      .required('Ingresa tu número de identificación')
  }),
  cellPhoneNumber: yup
    .string()
    .trim()
    .matches(/^[1-9]\d{9}$/, 'El número de teléfono debe ser válido')
    .required('Ingresa el número de teléfono'),
  terms: yup.boolean().oneOf([true], 'Este campo es requerido.'),
  habeasData: yup.boolean().oneOf([true], 'Este campo es requerido.')
});

const ConstructorContactStep = ({
  constructorInfo = {},
  formId,
  setGoBack,
  setCurrentStep: setVisualStepperIndex,
  setNextButtonText,
  setCanSubmit
}) => {
  const classes = useStyles();
  const recaptchaContainerRef = useRef(null);

  const currentStepIndex = useContext(CurrentStepIndexContext);
  const setCurrentStep = useContext(SetCurrentStepIndexContext);
  const setData = useContext(StepperDataDispatchContext);
  const stepperData = useContext(StepperDataContext);

  const currentUser = useContext(UserContext);
  const {
    captchaRef: formCaptchaRef,
    handlers: formCaptchaHandlers,
    captchaToken: formCaptcha,
    loading: loadingCaptcha
  } = useCaptcha();

  const businessIdentificationType =
    IDENTIFICATION_OPTION_FULL_LABEL[
      constructorInfo.businessIdentificationType
    ];

  const {
    register,
    errors,
    control,
    formState: { isValid },
    handleSubmit
  } = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
    shouldFocusError: true,
    defaultValues: {
      terms: false,
      habeasData: false,
      email: currentUser ? currentUser.email : '',
      fullName: '',
      identificationType: 'CC',
      identification: '',
      cellPhoneNumber: currentUser ? currentUser.phone : '',
      ...stepperData
    }
  });

  const onForward = values => {
    setData(data => ({ ...constructorInfo, ...data, ...values }));
    setCurrentStep(step => step + 1);
  };

  const handleCopyPaste = evt => {
    evt.preventDefault();
  };

  useEffect(() => {
    setNextButtonText('Siguiente');
    setVisualStepperIndex(currentStepIndex);
    setGoBack({});
  }, [setGoBack, setVisualStepperIndex, currentStepIndex, setNextButtonText]);

  useEffect(() => {
    setCanSubmit(isValid && !loadingCaptcha && Boolean(formCaptcha));
  }, [isValid, formCaptcha, loadingCaptcha, setCanSubmit]);

  return (
    <div className={classes.container}>
      <Typography className={classes.title}>Información de contacto</Typography>
      <form id={formId} onSubmit={handleSubmit(onForward)}>
        <Grid container spacing={2}>
          {constructorInfo && (
            <>
              <Grid item sm={4} xs={12}>
                <TextInput
                  label="Constructora"
                  value={constructorInfo.businessName}
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid item sm={4} xs={12}>
                <TextInput
                  label={businessIdentificationType}
                  value={constructorInfo.businessIdentification}
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid item sm={4} xs={12}>
                <TextInput
                  label="Proyecto"
                  value={constructorInfo.projectName}
                  fullWidth
                  disabled
                />
              </Grid>
            </>
          )}
          <Grid item sm={4} xs={12}>
            <TextInput
              label="Nombre completo"
              inputProps={{ maxLength: 150 }}
              inputRef={register}
              fullWidth
              name="fullName"
              helperText={errors.fullName && errors.fullName.message}
              error={Boolean(errors.fullName)}
            />
          </Grid>
          <Grid item sm={4} xs={12}>
            <ControlledSelectInput
              label="Tipo de identificación"
              name="identificationType"
              control={control}
              options={IDENTIFICATION_OPTIONS}
              fullWidth
            />
          </Grid>
          <Grid item sm={4} xs={12}>
            <TextInput
              label="Identificación"
              inputRef={register}
              fullWidth
              inputProps={{ maxLength: 15 }}
              name="identification"
              helperText={
                errors.identification && errors.identification.message
              }
              error={Boolean(errors.identification)}
            />
          </Grid>
          <Grid item sm={4} xs={12}>
            <TextInput
              label="Correo"
              inputProps={{ maxLength: 100 }}
              inputRef={register}
              fullWidth
              name="email"
              helperText={errors.email && errors.email.message}
              error={Boolean(errors.email)}
              onCut={handleCopyPaste}
              onCopy={handleCopyPaste}
              onPaste={handleCopyPaste}
            />
          </Grid>
          <Grid item sm={4} xs={12}>
            <TextInput
              label="Validar correo"
              inputRef={register}
              fullWidth
              name="validateEmail"
              helperText={errors.validateEmail && errors.validateEmail.message}
              error={Boolean(errors.validateEmail)}
              inputProps={{
                autoComplete: 'new-password',
                maxLength: 100
              }}
              onCut={handleCopyPaste}
              onCopy={handleCopyPaste}
              onPaste={handleCopyPaste}
            />
          </Grid>
          <Grid item sm={4} xs={12}>
            <TextInput
              label="Celular"
              type="tel"
              inputRef={register}
              fullWidth
              inputProps={{ maxLength: 10 }}
              name="cellPhoneNumber"
              helperText={
                errors.cellPhoneNumber && errors.cellPhoneNumber.message
              }
              error={Boolean(errors.cellPhoneNumber)}
            />
          </Grid>
          <Grid ref={recaptchaContainerRef} item sm={4} xs={12}>
            <Recaptcha
              captchaRef={formCaptchaRef}
              referenceEl={recaptchaContainerRef}
              heightScale={0.9}
              locale="es"
              onResolved={formCaptchaHandlers.onResolved}
              onExpired={formCaptchaHandlers.onExpired}
              onError={formCaptchaHandlers.onError}
              onLoaded={formCaptchaHandlers.onLoaded}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <ControlledCheckbox
                  id="Pqr_input_terms"
                  name="terms"
                  control={control}
                />
              }
              label={
                <Typography className={classes.termsText}>
                  He leído y acepto los{' '}
                  <a
                    className={classes.link}
                    href={`${ROUTE_NAMES.information}/${
                      TERMS_ROUTES[TERMS.introduction]
                    }`}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    términos y condiciones
                  </a>
                </Typography>
              }
            />
            <ErrorMessage
              errors={errors}
              name="terms"
              message={errors.terms}
              as={<Typography color="error" />}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <ControlledCheckbox
                  id="Pqr_input_terms"
                  name="habeasData"
                  control={control}
                />
              }
              label={
                <Typography className={classes.termsText}>
                  He leído, acepto y autorizo el{' '}
                  <a
                    className={classes.link}
                    href={PrivacyPoliciesLink}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    tratamiento de mis datos personales
                  </a>
                </Typography>
              }
            />
            <ErrorMessage
              errors={errors}
              name="habeasData"
              message={errors.habeasData}
              as={<Typography color="error" />}
            />
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(6)
  },
  title: {
    fontSize: 18,
    fontWeight: 700
  },
  itemContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2)
  },
  itemText: {
    fontSize: 16,
    textAlign: 'center',
    paddingRight: theme.spacing(),
    [theme.breakpoints.down('sm')]: {
      fontSize: 14,
      textAlign: 'left'
    }
  },
  inputContainerItem: {
    [theme.breakpoints.up('sm')]: {
      paddingRight: theme.spacing(2)
    }
  },
  termsText: {
    fontSize: 11,
    [theme.breakpoints.up('sm')]: {
      fontSize: 12
    }
  },
  link: {
    color: '#424242',
    fontWeight: 500
  },
  termsContainer: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing()
    }
  },
  sweetAlertText: {
    fontSize: 14
  },
  habeasDataContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing()
    }
  },
  dialogText: {
    [theme.breakpoints.up('sm')]: {
      fontSize: 14
    }
  },
  textBold: {
    fontWeight: 600
  },
  baseDialogActions: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      padding: theme.spacing(2)
    },
    justifyContent: 'space-between',
    padding: theme.spacing(2, 4)
  },
  actionButton: {
    '&:nth-child(2)': {
      margin: 0
    },
    [theme.breakpoints.down('sm')]: {
      '&:first-child': {
        marginTop: theme.spacing(2),
        order: 1
      },
      '&:nth-child(2)': {
        order: 0
      }
    }
  }
}));

export default ConstructorContactStep;
